import {
    faBorderAll,
    faChalkboardTeacher,
    faEdit,
    faFileDownload,
    faFileInvoice,
    faMapPin,
    faPaste,
    faPlus,
    faPowerOff,
    faTags,
    faTicketAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownload } from '../../../hooks';
import { useApi } from '../../../hooks/useApi';
import { useModal } from '../../../hooks/useModal';
import { useClubSlug } from '../../Clubs/hooks';
import { Action, Column, DataTable, DataTableContainer, Filter, TableActionButton } from '../../DataTable';
import { ActionCallbackProps } from '../../DataTable/DataTable';
import { PaymentStatusButton } from '../../MollieConnect';
import { convertBlockCode } from '../helpers';
import { Voucher } from '../types';
import VoucherStatus from './VoucherStatus';

const VoucherForm = dynamic(() => import('../VoucherForm'));
const DiscountVoucherForm = dynamic(() => import('../DiscountVoucherForm'));
const CardVoucherForm = dynamic(() => import('../CardVoucherForm'));
export const VoucherValueManager = dynamic(() => import('../VoucherValueManager'));

const VoucherList = () => {
    const { t } = useTranslation('voucher');
    const clubSlug = useClubSlug();
    const asyncListRef = useRef(null);
    const api = useApi();
    const download = useDownload();
    const [actionsLoading, setActionsLoading] = useState(false);
    const [setModal, resetModal] = useModal();

    const renderScopeIcons = useCallback((voucher: Voucher) => {
        const icons = [];
        if (voucher.scopes.includes('events')) icons.push(faTicketAlt);
        if (voucher.scopes.includes('space_bookings')) icons.push(faMapPin);
        if (voucher.scopes.includes('space_manager')) icons.push(faBorderAll);
        if (voucher.scopes.includes('trainer_bookings')) icons.push(faChalkboardTeacher);
        return icons;
    }, []);

    const renderTypeName = useCallback(
        (voucher: Voucher) => {
            switch (voucher.type) {
                case 'discount':
                    return t('list.typeName.discount');
                case 'card':
                    return t('list.typeName.card');
                default:
                    return t('list.typeName.value');
            }
        },
        [t]
    );

    const renderCode = useCallback((voucher: Voucher) => {
        switch (voucher.type) {
            case 'discount':
                return voucher.code?.toUpperCase();
            default:
                return convertBlockCode(voucher.code);
        }
    }, []);

    const columns: Column<Voucher>[] = useMemo(
        () => [
            {
                uid: 'code',
                name: t('list.columns.code'),
                renderer: (code, item) => (
                    <div className="tw-flex tw-flex-col">
                        <span className="tw-text-xs tw-italic tw-text-accents-6">
                            # {item.id} {item.refund ? t('list.codeRefund') : t('list.codePurchase')} (
                            {renderTypeName(item)})
                        </span>
                        <span className="tw-my-3 tw-text-lg tw-font-bold">{renderCode(item)}</span>
                        <span className="tw-flex tw-gap-4 tw-text-accents-6">
                            {renderScopeIcons(item).map((icon, i) => (
                                <FontAwesomeIcon icon={icon} key={i} />
                            ))}
                        </span>
                    </div>
                )
            },
            {
                uid: 'value',
                name: t('list.columns.info'),
                allowsSorting: true,
                renderer: (_, item) => <VoucherStatus voucher={item} />
            },
            {
                uid: 'price',
                name: t('list.columns.paymentInfo'),
                renderer: (_, item?) =>
                    item.paymentId && (
                        <PaymentStatusButton paymentId={item.paymentId} paymentStatus={item.paymentStatus} />
                    )
            },
            {
                uid: 'actions',
                align: 'end',
                name: t('list.columns.actions')
            }
        ],
        [t, renderTypeName, renderCode, renderScopeIcons]
    );

    const handleOpenVoucherForm = useCallback(() => {
        setModal({
            title: t('list.createValueVoucher'),
            content: (
                <VoucherForm
                    callback={() => {
                        asyncListRef?.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, [t]);

    const handleOpenDiscountVoucherForm = useCallback(() => {
        setModal({
            title: t('list.createDiscountVoucher'),
            content: (
                <DiscountVoucherForm
                    callback={() => {
                        asyncListRef?.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, [t]);

    const handleOpenCardVoucherForm = useCallback(() => {
        setModal({
            title: t('list.createCardVoucher'),
            content: (
                <CardVoucherForm
                    callback={() => {
                        asyncListRef?.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, [t]);

    const handleDownload = useCallback(
        (voucher: Voucher) => {
            setActionsLoading(true);
            void api({
                url: `club/${clubSlug}/voucher/${voucher.id}/download`,
                method: 'GET',
                responseType: 'blob',
                onSuccess: (data) => download(data, `Gutschein_${voucher.id}.pdf`),
                onBefore: () => setActionsLoading(false)
            });
        },
        [download, clubSlug]
    );

    const handleToggleVoucherStatus = useCallback((voucher: Voucher) => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/voucher/${voucher.id}/toggle`,
            method: 'GET',
            onSuccess: () => asyncListRef?.current?.reload(),
            onBefore: () => setActionsLoading(false)
        });
    }, []);

    const handleGenerateInvoices = useCallback(({ filter, selection }: ActionCallbackProps) => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/voucher/invoice`,
            method: 'POST',
            data: { filter, selection },
            muted: true,
            onBefore: () => setActionsLoading(false)
        });
    }, []);

    const handleCopyCode = useCallback(
        (voucher: Voucher) => {
            if (!navigator.clipboard) {
                return;
            }

            navigator.clipboard.writeText(voucher.code.toUpperCase()).then((r) => alert(t('list.codeCopiedAlert')));
        },
        [t]
    );

    const showVoucherValueManager = useCallback(
        (voucher: Voucher) => {
            setModal({
                title: t('list.manageVoucher'),
                content: (
                    <VoucherValueManager
                        voucher={voucher}
                        callback={() => {
                            asyncListRef?.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [t]
    );

    const showCardVoucherForm = useCallback(
        (voucher: Voucher) => {
            setModal({
                title: t('list.editCardVoucher'),
                content: (
                    <CardVoucherForm
                        voucher={voucher}
                        callback={() => {
                            asyncListRef?.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [t]
    );

    const actions = useMemo(
        (): Action[] => [
            {
                key: 'invoices',
                label: t('list.generateInvoice'),
                icon: faFileInvoice,
                action: handleGenerateInvoices
            }
        ],
        [t, handleGenerateInvoices]
    );

    const filters: Filter[] = useMemo(
        () => [
            {
                filterName: t('list.filters.type'),
                filterKey: 'type',
                filterType: 'select',
                options: [
                    { value: 'card', label: t('type.card') },
                    { value: 'value', label: t('list.filters.typeOptions.value') },
                    { value: 'discount', label: t('type.discount') }
                ]
            },
            {
                filterName: t('list.filters.voucherNumber'),
                filterKey: 'id',
                filterType: 'text'
            },
            {
                filterName: 'Status',
                filterKey: 'open',
                filterType: 'select',
                options: [
                    { value: 'true', label: 'Offen (nicht vollständig eingelöst)' },
                    { value: 'false', label: 'Eingelöst' }
                ]
            },
            {
                filterName: 'Erstelldatum',
                filterKey: 'created_at',
                filterType: 'date'
            }
        ],
        [t]
    );

    return (
        <DataTableContainer>
            <div className="icon={<FontAwesomeIcon icon={faPlus}/> } tw-grid tw-grid-cols-3 tw-gap-4">
                <Button icon={<FontAwesomeIcon icon={faPlus} />} onPress={() => handleOpenVoucherForm()}>
                    {t('list.createValueVoucher')}
                </Button>
                <Button icon={<FontAwesomeIcon icon={faPlus} />} onPress={() => handleOpenDiscountVoucherForm()}>
                    {t('list.createDiscountVoucher')}
                </Button>
                <Button icon={<FontAwesomeIcon icon={faPlus} />} onPress={() => handleOpenCardVoucherForm()}>
                    {t('list.createCardVoucher')}
                </Button>
            </div>
            <DataTable
                tableProps={{
                    showSelectionCheckboxes: true,
                    selectionMode: 'multiple',
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                filters={filters}
                fetchUrl={`club/${clubSlug}/voucher`}
                actionsLoading={actionsLoading}
                actions={actions}
                columns={columns}
                asyncListRef={asyncListRef}
                rowActions={(item: Voucher) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faPowerOff}
                            tooltip={item.is_active ? t('list.deactivateVoucher') : t('common.activateVoucher')}
                            color={item.is_active ? 'success' : 'error'}
                            onPress={() => handleToggleVoucherStatus(item)}
                        />
                        <TableActionButton
                            icon={faPaste}
                            color="light"
                            tooltip={t('list.copyCode')}
                            onPress={() => handleCopyCode(item)}
                        />
                        {item.type !== 'discount' && (
                            <>
                                <TableActionButton
                                    icon={faFileInvoice}
                                    tooltip={t('list.generateInvoice')}
                                    color="light"
                                    onPress={() => handleGenerateInvoices({ selection: [String(item.id)] })}
                                />
                                <TableActionButton
                                    icon={faFileDownload}
                                    color="light"
                                    tooltip={t('list.download')}
                                    onPress={() => handleDownload(item)}
                                />
                                {item.value > item.value_used && (
                                    <TableActionButton
                                        icon={faTags}
                                        tooltip={t('list.manageValue')}
                                        color="warning"
                                        onPress={() => showVoucherValueManager(item)}
                                    />
                                )}
                            </>
                        )}
                        {item.type === 'card' && (
                            <TableActionButton
                                icon={faEdit}
                                color="warning"
                                tooltip={t('list.editCard')}
                                onPress={() => showCardVoucherForm(item)}
                            />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default VoucherList;
